import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import Collapse from "@mui/material/Collapse";

import { RedBadge } from "../../../../components/Badge";
import type { ButtonProps } from "../../../../components/Button";
import OverflowTooltip from "../../../../components/OverflowTooltip";
import CheckCircleIcon from "../../../../icons/CheckCircle";
import ExpandIcon from "../../../../icons/Expand";
import FlagIcon from "../../../../icons/Flag";
import SidebarButtonBase from "../../../../components/SidebarButton";
import colors from "../../../../theme/colors";
import Warning from "../../../../icons/Warning";

type Props = {
  title: string;
  step: number;
  activeStep?: number;
  flag: boolean;
  complete: boolean;
  onClick?: () => void;
  sopDeviations?: string | null;
  children?: Omit<Props, "children">[];
};

const SidebarButton = ({
  title,
  step,
  activeStep,
  flag,
  complete,
  onClick,
  children,
  sopDeviations,
}: Props) => {
  const hasActiveChild =
    children?.some((c) => c.step === c.activeStep) || false;
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (hasActiveChild) {
      setExpanded(true);
    }
  }, [hasActiveChild]);

  const { formState } = useFormContext() || {};
  const { errors } = formState || { errors: {} };

  const countForStep = (step: number) => {
    if (!errors["metadata"]) {
      return 0;
    }

    let total = 0;
    errors["metadata"].forEach(({ items = [], ...keys }: any) => {
      if (step === -1) {
        total += Object.keys(keys).length;
      } else if (items[step]) {
        total += Object.keys(items[step]).length;
      }
    });

    return total;
  };

  const getClassName = (active: boolean, hasChildren: boolean) => {
    let className = "";
    if (active) {
      className += " active";
    }
    if (!hasChildren) {
      className += " noChildren";
    }
    return className;
  };

  const count = countForStep(step);

  return children ? (
    <Collapse
      in={expanded || hasActiveChild}
      collapsedSize={47}
      className="small"
    >
      <>
        <ButtonWithoutWatch
          className={getClassName(false, children.length > 0)}
          size="small"
          color="text"
          onClick={() => setExpanded(!expanded || hasActiveChild)}
          startIcon={
            <ExpandIcon
              style={
                expanded || hasActiveChild ? { transform: "rotate(90deg)" } : {}
              }
            />
          }
          sopDeviations={sopDeviations}
          flag={flag}
        >
          <OverflowTooltip title={title}>
            <span>{title}</span>
          </OverflowTooltip>
        </ButtonWithoutWatch>

        {children.map(
          (
            { title, step, activeStep, onClick, flag, complete, sopDeviations },
            i
          ) => (
            <ButtonWithoutWatch
              className={getClassName(step === activeStep, false)}
              key={i}
              href={`#tc-section-${step}`}
              size="small"
              color="text"
              endIcon={
                complete ? (
                  <CheckCircleIcon
                    style={{
                      width: 20,
                      height: 20,
                      color: colors.accent.green,
                    }}
                  />
                ) : countForStep(step) > 0 ? (
                  <RedBadge badgeContent={`${countForStep(step)}`} />
                ) : null
              }
              onClick={onClick}
              flag={flag}
              sopDeviations={sopDeviations}
              style={{ paddingLeft: "2rem" }}
            >
              <OverflowTooltip title={title}>
                <span>{title}</span>
              </OverflowTooltip>
            </ButtonWithoutWatch>
          )
        )}
      </>
    </Collapse>
  ) : (
    <ButtonWithoutWatch
      className={getClassName(step === activeStep, !!children)}
      href={`#tc-section-${step}`}
      size="small"
      color="text"
      onClick={onClick}
      endIcon={
        complete ? (
          <CheckCircleIcon
            style={{
              width: 20,
              height: 20,
              color: colors.accent.green,
            }}
          />
        ) : count > 0 ? (
          <RedBadge badgeContent={`${count}`} />
        ) : null
      }
      flag={flag}
      sopDeviations={sopDeviations}
      style={{ paddingLeft: "0.75rem" }}
    >
      <OverflowTooltip title={title}>
        <span>{title}</span>
      </OverflowTooltip>
    </ButtonWithoutWatch>
  );
};

type ButtonWithoutWatchProps = {
  flag: boolean;
  sopDeviations?: string | null;
} & ButtonProps;

const ButtonWithoutWatch = ({
  flag,
  sopDeviations,
  startIcon,
  style,
  ...props
}: ButtonWithoutWatchProps) => {
  const paddingLeft =
    !flag && !startIcon
      ? `calc(${style?.paddingLeft || "0rem"} + 1.5rem)`
      : style?.paddingLeft;

  return (
    <SidebarButtonBase
      {...props}
      style={{ ...style, paddingLeft, height: 44 }}
      endIcon={
        props.endIcon ||
        (sopDeviations ? (
          <Warning style={{ color: colors.accent.burntYellow }} />
        ) : null)
      }
      startIcon={
        flag ? (
          <FlagIcon style={{ color: colors.accent.burntYellow }} />
        ) : (
          startIcon
        )
      }
    />
  );
};

export default SidebarButton;
